/**
 * Contextual first-steps tips: each one appears only when it is relevant, stays until the player has done
 * the thing (or, for the informational ones, until it has been on screen long enough), and is then
 * remembered as done. One at a time, short, worded for the keyboard or the touch controls. Pure;
 * unit-tested in onboarding.test.ts. The system feeds `TipFacts` once per frame.
 */

export interface TipFacts {
  touch: boolean;
  /** Left-handed touch layout: the stick is under the right thumb. */
  touchLeftHanded: boolean;
  mode: 'foot' | 'driving' | 'other';
  /** Seconds of play this session (the HUD up). */
  time: number;
  /** Metres walked / seconds run / metres driven, counted by the system this session. */
  walked: number;
  ran: number;
  driven: number;
  /** An empty car within a few metres. */
  nearCar: boolean;
  enteredCar: boolean;
  heat: number;
  mapOpened: boolean;
  bookOpened: boolean;
  zoomChanged: boolean;
  jobsDone: number;
  fenceKnown: boolean;
  nearFence: boolean;
  /**
   * The law is already being talked about elsewhere on the screen — the online law strip, or the "what
   * next" clipping saying "Lose the law" — so the law tip stays quiet (one owner for that copy).
   */
  lawShown?: boolean;
  /** At the wheel of a car the police will know as stolen (a theft; not your own, not a loaner). */
  stolenCar?: boolean;
  /** In the shared city (the stolen-car tip names Halloran's loaner there). */
  online?: boolean;
  /**
   * A job is on (offline a mission, online a courier job or a run): its own clipping is talking, so the tips
   * that are about finding your way round (the book) wait for the gap between jobs.
   */
  onJob?: boolean;
  /** The player's own keys for the actions a tip names (labels from `keyLabel`; defaults Space / Shift). */
  keys?: { handbrake: string; run: string };
}

export interface TipDef {
  id: string;
  when(f: TipFacts): boolean;
  done(f: TipFacts): boolean;
  text(f: TipFacts): string;
  /** Informational tips count as done after this many seconds on screen. */
  maxShow?: number;
}

export const TIPS: readonly TipDef[] = [
  {
    id: 'heat',
    when: (f) => f.heat > 0 && !f.lawShown,
    done: (f) => f.heat <= 0,
    maxShow: 16,
    text: () => 'The police are after you. Get out of their sight — corners, alleys — and keep going. The badge top right clears once they lose you.',
  },
  {
    id: 'walk',
    when: (f) => f.mode === 'foot',
    done: (f) => f.walked >= 8,
    text: (f) => (f.touch ? `Drag your thumb low on the ${f.touchLeftHanded ? 'right' : 'left'} of the screen to walk.` : 'Walk with W A S D (or the arrow keys).'),
  },
  {
    id: 'car',
    when: (f) => f.mode === 'foot' && f.nearCar && f.walked >= 3,
    done: (f) => f.enteredCar,
    text: (f) =>
      f.touch
        ? 'Tap USE next to a car to get in. Locked ones take a few seconds to break into.'
        : 'Press E next to a car to get in. Locked ones take a few seconds to break into.',
  },
  {
    id: 'drive',
    when: (f) => f.mode === 'driving',
    done: (f) => f.driven >= 80,
    text: (f) =>
      !f.touch
        ? `W to go, S to brake and reverse, A / D to steer. ${f.keys?.handbrake || 'Space'} is the handbrake.`
        : 'Point the stick where you want to go and the car steers that way. GAS drives, BRAKE stops — keep holding BRAKE and it backs up where you point.',
  },
  {
    // Why the police come after a stolen car, and how to be rid of them (the first theft online used to end in
    // NICKED forty seconds later with nobody having said why — gameplay analysis §7.2).
    id: 'stolen',
    when: (f) => f.mode === 'driving' && !!f.stolenCar,
    done: () => false,
    maxShow: 15,
    text: (f) =>
      f.online
        ? 'This car is stolen: it gets reported, and any copper who sees it gives chase. Lose them, then ditch it or have Pike respray it (the spray gun on the map). Halloran’s loaner is never reported.'
        : 'This car is stolen: it gets reported, and any copper who sees it gives chase. Lose them, then ditch it or have Pike respray it (the spray gun on the map).',
  },
  {
    id: 'run',
    when: (f) => f.mode === 'foot' && f.walked >= 8,
    done: (f) => f.ran >= 1.2,
    maxShow: 12,
    text: (f) => (f.touch ? 'Hold RUN while you walk to run.' : `Hold ${f.keys?.run || 'Shift'} while you walk to run.`),
  },
  {
    id: 'book',
    // Between jobs: it used to land 20 s in, on the walk to the first job's car (and online at the first board).
    when: (f) => !f.onJob && f.walked >= 8 && f.time > 20,
    done: (f) => f.bookOpened,
    maxShow: 12,
    text: (f) =>
      f.touch
        ? 'Your little black book (top right): jobs, messages and contacts. The bleeper counts new messages.'
        : 'Your little black book: jobs, messages and contacts. Press I. The bleeper top right counts new messages.',
  },
  {
    id: 'map',
    when: (f) => f.driven >= 80 || f.time > 150,
    done: (f) => f.mapOpened,
    maxShow: 10,
    text: (f) => (f.touch ? 'Lost? Tap MAP (top right) for the whole borough, with a key.' : 'Lost? Press M for the whole borough, with a key.'),
  },
  {
    id: 'fence',
    when: (f) => f.jobsDone >= 1 && f.fenceKnown,
    done: (f) => f.nearFence,
    maxShow: 12,
    text: () => 'Need more than your fists? Sid Marlowe, the fence at Gorringe’s scrap yard (the revolver on the map), sells for cash.',
  },
  {
    id: 'zoom',
    when: (f) => f.time > 240,
    done: (f) => f.zoomChanged,
    maxShow: 9,
    text: () => 'Camera too close or too far? Pause → Settings → Camera zoom.',
  },
];

/** The basics an existing save has obviously done already. */
export const BASIC_TIPS = ['walk', 'run', 'car', 'drive', 'map', 'book'];

const GAP_S = 2.5;
/** A tip whose condition lapses briefly (walked past the car) stays up this long before it goes. */
const LINGER_S = 1.5;

export class TipEngine {
  readonly done = new Set<string>();
  current: { id: string; shown: number; lapsed: number } | null = null;
  private gap = 0;

  constructor(private readonly tips: readonly TipDef[] = TIPS) {}

  /** Advance one frame. Returns the ids completed this frame (to persist). */
  update(f: TipFacts, dt: number, enabled: boolean): string[] {
    const finished: string[] = [];
    // Things done count even while tips are off or another tip is showing (no nagging afterwards).
    for (const t of this.tips) {
      if (!this.done.has(t.id) && t.done(f) && (t.id !== 'heat' || this.current?.id === 'heat')) {
        this.done.add(t.id);
        finished.push(t.id);
      }
    }
    if (this.current && this.done.has(this.current.id)) {
      this.current = null;
      this.gap = GAP_S;
    }
    if (!enabled) {
      this.current = null;
      return finished;
    }
    if (this.current) {
      const def = this.tips.find((t) => t.id === this.current!.id)!;
      this.current.shown += dt;
      if (def.maxShow !== undefined && this.current.shown >= def.maxShow) {
        this.done.add(def.id);
        finished.push(def.id);
        this.current = null;
        this.gap = GAP_S;
      } else if (!def.when(f)) {
        this.current.lapsed += dt;
        if (this.current.lapsed > LINGER_S) this.current = null;
      } else this.current.lapsed = 0;
      // An urgent tip (the law) cuts in.
      const heat = this.tips.find((t) => t.id === 'heat');
      if (this.current && this.current.id !== 'heat' && heat && !this.done.has('heat') && heat.when(f)) this.current = { id: 'heat', shown: 0, lapsed: 0 };
      return finished;
    }
    if (this.gap > 0) {
      this.gap -= dt;
      return finished;
    }
    const next = this.tips.find((t) => !this.done.has(t.id) && t.when(f));
    if (next) this.current = { id: next.id, shown: 0, lapsed: 0 };
    return finished;
  }

  text(f: TipFacts): string | null {
    if (!this.current) return null;
    return this.tips.find((t) => t.id === this.current!.id)?.text(f) ?? null;
  }
}
